import React, { useEffect, useMemo } from 'react';
import { Tooltip } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Card, CardBody, Col, Row } from 'reactstrap';
import { createSelector } from 'reselect';

import { ODataGrid } from '../../../../Components/OElements/ODataGrid';
import { getWarehouseResidueStock } from '../../../../slices/warehouseResidueStock/thunk';
import { useRespelEstablishmentToolbarGate } from '../hooks/useRespelEstablishmentToolbarGate';
import {
	RESPEL_NO_WAREHOUSE_ALERT_COPY,
	RESPEL_NO_WAREHOUSE_DETAIL_COPY,
} from '../respelNoWarehouseCopy';
import {
	formatDueDateLabelFromIso,
	getRespelIntakeExpiryBadgeClass,
} from '../utils/respelIntakeLegalExpiry';

const isMongoObjectIdString = value =>
	typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

/**
 * Listado plano de stock RESPEL consolidado por (bodega, residuo) (D-013).
 * Filtrado por `establishment_id` (establecimiento de la ruta / selector global).
 */
const StockList = ({ establishmentId }) => {
	const dispatch = useDispatch();
	const establishmentReady = useMemo(
		() => isMongoObjectIdString(establishmentId),
		[establishmentId],
	);

	const selector = createSelector(
		state => state.WarehouseResidueStock,
		stock => ({
			stockList: stock.stockList,
			isLoadingList: stock.isLoadingList,
			listError: stock.error,
			stockMutationEpoch: stock.stockMutationEpoch,
		})
	);
	const { stockList, isLoadingList, listError, stockMutationEpoch } =
		useSelector(selector);

	const {
		showNoWarehouseAlert,
		warehouseVerifyError,
		warehouseCheckLoading,
		refreshEnabled,
	} = useRespelEstablishmentToolbarGate({
		establishmentId,
		establishmentReady,
		list: stockList || [],
		isLoadingList,
		listError,
	});

	const stockParams = useMemo(
		() =>
			establishmentReady
				? {
						respel_only: true,
						establishment_id: establishmentId,
					}
				: null,
		[establishmentId, establishmentReady],
	);

	useEffect(() => {
		if (!stockParams) return;
		dispatch(getWarehouseResidueStock(stockParams));
	}, [dispatch, stockParams, stockMutationEpoch]);

	const columns = useMemo(
		() => [
			{
				header: 'Bodega',
				accessorKey: 'warehouse.name',
				size: 'auto',
				Cell: ({ row }) =>
					row.original.warehouse?.name ||
					row.original.warehouse_name ||
					row.original.warehouse_id ||
					'-',
			},
			{
				header: 'Residuo',
				accessorKey: 'residue.name',
				size: 'auto',
				Cell: ({ row }) =>
					row.original.residue?.name ||
					row.original.residue_name ||
					row.original.residue_id ||
					'-',
			},
			{
				header: 'Categoría',
				accessorKey: 'residue.residue_category.name',
				size: 'auto',
				visible: false,
				Cell: ({ row }) =>
					row.original.residue?.residue_category?.name ||
					row.original.residue_category_name ||
					'-',
			},
			{
				header: 'Kg disponibles',
				accessorKey: 'kilograms_total',
				size: 'auto',
				summary: true,
				Cell: ({ row }) => {
					const kg = Number(row.original.kilograms_total);
					return Number.isFinite(kg)
						? kg.toLocaleString('es-CL', { maximumFractionDigits: 2 })
						: '-';
				},
			},
			{
				header: 'Vencimiento',
				accessorKey: 'worst_legal_days_remaining',
				enableSorting: false,
				enableColumnFilter: false,
				size: 'auto',
				Header: () => (
					<span
						title='Menor cantidad de días restantes entre los ingresos con stock en esta bodega/residuo (180 días).'
					>
						Vencimiento
					</span>
				),
				Cell: ({ row }) => {
					const days = row.original.worst_legal_days_remaining;
					if (
						days === null ||
						days === undefined ||
						!Number.isFinite(Number(days))
					) {
						return '—';
					}
					const n = Number(days);
					const cls = getRespelIntakeExpiryBadgeClass(n);
					const dueLabel = formatDueDateLabelFromIso(
						row.original.worst_legal_due_date,
					);
					const title = dueLabel
						? `Último día del plazo (180 d, UTC): ${dueLabel}`
						: undefined;
					return (
						<span title={title} className={cls}>
							{n}
						</span>
					);
				},
			},
			{
				header: 'Último movimiento',
				accessorKey: 'last_movement_date',
				dataType: 'datetime',
				size: 'auto',
			},
			{
				header: 'Establecimiento',
				accessorKey: 'establishment.name',
				size: 'auto',
				visible: false,
				Cell: ({ row }) =>
					row.original.establishment?.name ||
					row.original.establishment_name ||
					row.original.establishment_id ||
					'-',
			},
		],
		[]
	);

	const gridData = establishmentReady ? stockList || [] : [];

	const refreshTooltip = useMemo(() => {
		if (!establishmentReady) return '';
		if (isLoadingList) return 'Esperando la carga del listado…';
		if (warehouseCheckLoading) return 'Verificando bodegas RESPEL…';
		if (warehouseVerifyError) return warehouseVerifyError;
		if (showNoWarehouseAlert) return RESPEL_NO_WAREHOUSE_DETAIL_COPY;
		if (!refreshEnabled) return 'Espere a completar la carga.';
		return 'Refrescar stock';
	}, [
		establishmentReady,
		isLoadingList,
		warehouseCheckLoading,
		warehouseVerifyError,
		showNoWarehouseAlert,
		refreshEnabled,
	]);

	const stockToolbar = useMemo(() => {
		if (!establishmentReady) return undefined;
		return () => (
			<div className='d-flex align-items-center justify-content-between gap-2 w-100 min-w-0'>
				<div className='d-flex justify-content-start gap-2 align-items-center flex-shrink-0'>
					<Tooltip label={refreshTooltip} position='top' withinPortal>
						<span className='d-inline-block'>
							<button
								type='button'
								className='btn d-flex align-items-center btn-tooltip-hover'
								disabled={!refreshEnabled}
								onClick={() =>
									dispatch(getWarehouseResidueStock(stockParams))
								}
							>
								<i
									className={`${window.Icons?.refresh || 'icon-refresh-cw'} label-icon icon-tooltop-size`}
								></i>
								{' '}
								Refrescar
							</button>
						</span>
					</Tooltip>
				</div>
				{showNoWarehouseAlert || warehouseVerifyError ? (
					<div className='min-w-0 flex-grow-1 ms-2'>
						{showNoWarehouseAlert ? (
							<div
								role='status'
								className='alert alert-warning small py-1 px-2 mb-0 text-truncate lh-sm'
							>
								{RESPEL_NO_WAREHOUSE_ALERT_COPY}
							</div>
						) : null}
						{warehouseVerifyError ? (
							<div
								role='alert'
								className='alert alert-danger small py-1 px-2 mb-0 text-truncate lh-sm'
							>
								{warehouseVerifyError}
							</div>
						) : null}
					</div>
				) : null}
			</div>
		);
	}, [
		dispatch,
		establishmentReady,
		refreshEnabled,
		refreshTooltip,
		showNoWarehouseAlert,
		stockParams,
		warehouseVerifyError,
	]);

	return (
		<React.Fragment>
			{!establishmentReady ? (
				<Alert color='warning' className='mb-3'>
					Establecimiento no válido o no seleccionado. Use el menú con un centro
					activo o el selector global.
				</Alert>
			) : null}
			<Row>
				<Col lg={12}>
					<Card>
						<CardBody>
							<ODataGrid
								columns={columns}
								data={gridData}
								isLoadingData={establishmentReady && isLoadingList}
								nameFile={'stock_respel'}
								customToolbarActions={stockToolbar}
							/>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</React.Fragment>
	);
};

export default StockList;
